import { CheckboxItem, Content, Group, Item, ItemIndicator, Label, Portal, RadioItem, Root, Separator, Sub, SubContent, SubTrigger, Trigger } from '@radix-ui/react-dropdown-menu';
import { type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';
const DropdownMenu = Root;
const DropdownMenuTrigger = Trigger;
const DropdownMenuGroup = Group;
const DropdownMenuPortal = Portal;
const DropdownMenuSub = Sub;
const DropdownMenuSubTrigger = forwardRef<ElementRef<typeof SubTrigger>, ComponentPropsWithoutRef<typeof SubTrigger> & {
  inset?: boolean;
}>(({
  className,
  inset,
  children,
  ...props
}, ref) => <SubTrigger ref={ref} className={cn('flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-primary-muted-fill data-[state=open]:bg-primary-muted-fill', inset && 'pl-8', className)} {...props}>
    {children}
    <Icon iconName="ChevronRight" className="ml-auto h-4 w-4" />
  </SubTrigger>);
DropdownMenuSubTrigger.displayName = SubTrigger.displayName;
const DropdownMenuSubContent = forwardRef<ElementRef<typeof SubContent>, ComponentPropsWithoutRef<typeof SubContent>>(({
  className,
  ...props
}, ref) => <SubContent ref={ref} className={cn('z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', className)} {...props} />);
DropdownMenuSubContent.displayName = SubContent.displayName;
const DropdownMenuContent = forwardRef<ElementRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({
  className,
  sideOffset = 4,
  ...props
}, ref) => <Portal>
    <Content ref={ref} sideOffset={sideOffset} className={cn('z-50 min-w-32 overflow-auto max-h-96 rounded-md border bg-popover p-1 text-popover-foreground shadow-md', 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', className)} {...props} />
  </Portal>);
DropdownMenuContent.displayName = Content.displayName;
const DropdownMenuItem = forwardRef<ElementRef<typeof Item>, ComponentPropsWithoutRef<typeof Item> & {
  inset?: boolean;
}>(({
  className,
  inset,
  ...props
}, ref) => <Item ref={ref} className={cn('relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-primary-muted-fill focus:text-primary-on-muted-fill data-disabled:pointer-events-none data-disabled:opacity-50', inset && 'pl-8', className)} {...props} />);
DropdownMenuItem.displayName = Item.displayName;
const DropdownMenuCheckboxItem = forwardRef<ElementRef<typeof CheckboxItem>, ComponentPropsWithoutRef<typeof CheckboxItem>>(({
  className,
  children,
  checked,
  ...props
}, ref) => <CheckboxItem ref={ref} className={cn('relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-primary-muted-fill focus:text-primary-on-muted-fill data-disabled:pointer-events-none data-disabled:opacity-50', className)} checked={checked} {...props}>
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ItemIndicator>
        <Icon iconName="Check" />
      </ItemIndicator>
    </span>
    {children}
  </CheckboxItem>);
DropdownMenuCheckboxItem.displayName = CheckboxItem.displayName;
const DropdownMenuRadioItem = forwardRef<ElementRef<typeof RadioItem>, ComponentPropsWithoutRef<typeof RadioItem>>(({
  className,
  children,
  ...props
}, ref) => <RadioItem ref={ref} className={cn('relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-primary-muted-fill focus:text-primary-on-muted-fill data-disabled:pointer-events-none data-disabled:opacity-50', className)} {...props}>
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ItemIndicator>
        <Icon iconName="DotIcon" className="fill-current" />
      </ItemIndicator>
    </span>
    {children}
  </RadioItem>);
DropdownMenuRadioItem.displayName = RadioItem.displayName;
const DropdownMenuLabel = forwardRef<ElementRef<typeof Label>, ComponentPropsWithoutRef<typeof Label> & {
  inset?: boolean;
}>(({
  className,
  inset,
  ...props
}, ref) => <Label ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)} {...props} />);
DropdownMenuLabel.displayName = Label.displayName;
const DropdownMenuSeparator = forwardRef<ElementRef<typeof Separator>, ComponentPropsWithoutRef<typeof Separator>>(({
  className,
  ...props
}, ref) => <Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />);
DropdownMenuSeparator.displayName = Separator.displayName;
const DropdownMenuShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';
export { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger };